import React, { useRef, useEffect } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { SortPopover } from './SortPopover';
import { SortMenu } from './SortMenu';
import { useMultiSort } from '../../hooks/useMultiSort';

interface SortField {
  id: string;
  name: string;
  type?: string;
}

interface SortButtonProps {
  fields: SortField[];
  onSortChange?: (sorts: Array<{ fieldId: string; direction: 'asc' | 'desc' }>, fullMatchesFirst: boolean) => void;
}

export const SortButton: React.FC<SortButtonProps> = ({ fields, onSortChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const onSortChangeRef = useRef(onSortChange);

  // Keep ref updated
  useEffect(() => {
    onSortChangeRef.current = onSortChange;
  }, [onSortChange]);

  const {
    state,
    clear,
    updateState,
    addSort,
    removeSort,
    updateSort,
    reorderSorts
  } = useMultiSort();

  // Notify parent whenever state changes
  useEffect(() => {
    if (onSortChangeRef.current) {
      onSortChangeRef.current(state.sorts, state.fullMatchesFirst);
    }
  }, [state]);

  const isActive = state.sorts.length > 0;

  const handleOpen = () => {
    setIsOpen(true);
    // Show advanced panel if there are already active sorts, otherwise show simple menu
    setShowAdvanced(state.sorts.length > 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowAdvanced(false);
  };

  const handleSelectField = (fieldId: string) => {
    // Transition to advanced panel with the selected field pre-populated
    addSort(fieldId, 'asc');
    setShowAdvanced(true);
  };

  const handleClear = () => {
    clear();
    setIsOpen(false);
    setShowAdvanced(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg transition-all outline-none ${
          isActive
            ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        }`}
      >
        <ArrowUpDown className="w-3.5 h-3.5" />
        <span>
          {isActive
            ? `Sort (${state.sorts.length})`
            : 'Sort'}
        </span>
      </button>

      {!showAdvanced ? (
        <SortMenu
          isOpen={isOpen}
          onClose={handleClose}
          anchorEl={buttonRef.current}
          fields={fields}
          onSelectField={handleSelectField}
        />
      ) : (
        <SortPopover
          isOpen={isOpen}
          onClose={handleClose}
          anchorEl={buttonRef.current}
          fields={fields}
          fullMatchesFirst={state.fullMatchesFirst}
          sorts={state.sorts}
          onFullMatchesFirstChange={(value) => updateState({ fullMatchesFirst: value })}
          onAddSort={() => addSort()}
          onRemoveSort={removeSort}
          onUpdateSort={updateSort}
          onReorderSorts={reorderSorts}
          onClear={handleClear}
        />
      )}
    </>
  );
};
